import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { TeamType } from "@redotech/redo-model/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import { useCustomAnalyticsReportsFeatureFlag } from "../../../../analytics/reports/custom/hooks/use-custom-analytics-reports-feature-flag";
import { OrderManagementSuiteStatusesContext } from "../../../../redo-suite/order-management-cloud";

export const ReturnsAnalyticsItem = memo(function ReturnsAnalyticsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  const { returnsEnabled, packageProtectionEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const hasPermissionToViewOrderFulfillment = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
  );
  const hasPermissionToViewReturnsClaims = useHasPermission(
    OrderManagementPermissions.VIEW_RETURN_AND_CLAIM_ANALYTICS,
  );
  const hasPermissionToViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );
  const { data: customReportsEnabled } = useCustomAnalyticsReportsFeatureFlag();

  const showReturnsClaims =
    (returnsEnabled || packageProtectionEnabled) &&
    hasPermissionToViewReturnsClaims;

  const is3PL = team.teamType === TeamType.FulfillmentService;
  const showReturnsProcessing = is3PL && hasPermissionToViewOrderFulfillment;
  const showManagedReturns =
    !!team.settings.redoManagedReturns?.enabled &&
    hasPermissionToViewOrderFulfillment;

  const returnsClaimsSelected = useMatch(
    `${routes.analyticsReturnsClaimsHref}/*`,
  );
  const reportsSelected = useMatch(`${routes.analyticsReportsHref}/*`);
  const returnsProcessingSelected = useMatch(
    `${routes.analyticsReturnsProcessingHref}/*`,
  );
  const managedReturnsSelected = useMatch(
    `${routes.analyticsManagedReturnsHref}/*`,
  );
  const customReportsHref = `${routes.analyticsReportsHref}/custom`;
  const anyChildSelected =
    returnsClaimsSelected ||
    reportsSelected ||
    returnsProcessingSelected ||
    managedReturnsSelected;

  const shouldShowAnalytics =
    showReturnsClaims ||
    hasPermissionToViewReports ||
    showReturnsProcessing ||
    showManagedReturns;

  if (!shouldShowAnalytics) {
    return null;
  }

  const defaultRoute = showReturnsClaims
    ? routes.analyticsReturnsClaimsHref
    : routes.analyticsReportsHref;

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      id="returns-analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReturnsClaimsHref }}
        friendlyName="Returns & claims"
        hidden={!showReturnsClaims}
        id="returns-analytics-returns-claims"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReportsHref }}
        friendlyName="Reports"
        hidden={!hasPermissionToViewReports}
        id="returns-analytics-reports"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: customReportsHref }}
        beta
        friendlyName="Custom reports"
        hidden={!hasPermissionToViewReports || !customReportsEnabled}
        id="returns-analytics-custom-reports"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsReturnsProcessingHref }}
        friendlyName="Returns processing"
        hidden={!showReturnsProcessing}
        id="returns-analytics-returns-processing"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsManagedReturnsHref }}
        friendlyName="Managed returns"
        hidden={!showManagedReturns}
        id="returns-analytics-managed-returns"
      />
    </RedoSecondaryNavItem>
  );
});
